import { Component, type ReactNode } from 'react';

/**
 * The render-exception boundary (issue #376).
 *
 * WHY A CLASS. React has no hook form for this: `getDerivedStateFromError` is
 * only reachable from a class component, and there is no supported alternative
 * at React 19.3. A thrown render is otherwise an unhandled error that unmounts
 * the whole tree — the blank-white-screen failure this issue is about — so the
 * class is the mechanism, not a style choice.
 *
 * WHERE IT SITS. Three instances, deliberately, because an error boundary
 * catches only its DESCENDANTS:
 *
 * - One inside `<main>`, keyed by `location.pathname`, so a route that throws
 *   is replaced by the localised 500 page while the site chrome (header, nav,
 *   footer, the not-legal-advice disclaimer) stays on screen and usable. The
 *   `key` is what makes it PER-ROUTE rather than once-per-session: React
 *   remounts the boundary on navigation, so leaving a broken route clears the
 *   error instead of pinning the 500 page onto every other page.
 * - One in `main.tsx` wrapping the whole `<App>` but INSIDE both providers, so
 *   a throw in the shell (a broken header or footer) still renders a localised
 *   page with the locale context intact.
 * - The OUTERMOST one in `main.tsx`, ABOVE `LocaleProvider` and
 *   `DocumentMetaProvider`. A provider that throws is an ANCESTOR of any
 *   boundary nested inside it, so only this instance can catch it; its fallback
 *   therefore takes its catalogue from the document's declared language rather
 *   than from the context that failed. (cross-review F001)
 *
 * The fallbacks cannot use `<Link>` or `useNavigate`, because the router may be
 * exactly what failed, so they recover with a real `<a href="/">` — a fresh
 * document, the most reliable recovery there is.
 *
 * WHAT IT MUST NEVER DO. Render, or put in an attribute, any part of the
 * thrown value's message. React error messages routinely echo the value that
 * broke rendering, which on this product's authenticated surfaces can be a
 * case note or a person's name — an error page that prints it turns a crash
 * into a disclosure. This class never receives the error at all:
 * `getDerivedStateFromError` takes no argument, and there is no
 * `componentDidCatch`. Reporting the component STACK (component types, no
 * rendered data) is the root's job — `root-errors.ts`, whose `onCaughtError`
 * replaces React's default handler and would otherwise log the full message.
 */
export interface AppErrorBoundaryProps {
  readonly children: ReactNode;
  /** Receives a reset callback so the fallback can offer a non-reloading retry. */
  readonly fallback: (reset: () => void) => ReactNode;
}

interface AppErrorBoundaryState {
  readonly failed: boolean;
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  override state: AppErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { failed: true };
  }

  private readonly reset = (): void => {
    this.setState({ failed: false });
  };

  override render(): ReactNode {
    if (this.state.failed) return this.props.fallback(this.reset);
    return this.props.children;
  }
}
